export type PreviewReadiness = 'foundation' | 'interactive-preview' | 'platform-preview';
export type PreviewGroup = 'start' | 'masterdata' | 'operations' | 'commercial' | 'collaboration' | 'system';

export interface PreviewMetric {
  readonly label: string;
  readonly value: string;
  readonly note: string;
}

export interface PreviewQueueItem {
  readonly title: string;
  readonly meta: string;
  readonly status: string;
  readonly priority: 'normal' | 'attention' | 'critical';
}

export interface PreviewModuleDefinition {
  readonly id: string;
  readonly name: string;
  readonly route: string;
  readonly permission: string;
  readonly group: PreviewGroup;
  readonly readiness: PreviewReadiness;
  readonly eyebrow: string;
  readonly description: string;
  readonly metrics: readonly PreviewMetric[];
  readonly workflow: readonly string[];
  readonly queue: readonly PreviewQueueItem[];
  readonly actions: readonly string[];
  readonly aliases?: readonly string[];
}

export interface CompleteCatalogEntry {
  readonly id: string;
  readonly name: string;
  readonly route: string;
  readonly group: PreviewGroup;
  readonly readiness: PreviewReadiness;
  readonly description: string;
  readonly aliases?: readonly string[];
}

const module = (
  definition: PreviewModuleDefinition,
): PreviewModuleDefinition => definition;

export const previewModuleDefinitions: readonly PreviewModuleDefinition[] = [
  module({
    id: 'orders',
    name: 'Vorgänge',
    route: '/vorgaenge',
    permission: 'orders.view',
    group: 'operations',
    readiness: 'interactive-preview',
    eyebrow: 'Zentrale Vorgangssteuerung',
    description: 'Service-, interne, Verkaufs- und Mietvorgänge in einem gemeinsamen, fortlaufenden Geschäftsfall.',
    metrics: [
      { label: 'Offene Vorgänge', value: '37', note: 'davon 8 heute fällig' },
      { label: 'Bereit zur Abrechnung', value: '6', note: 'vollständig zurückgemeldet' },
      { label: 'Ungeplante Einsätze', value: '4', note: 'Zuweisung erforderlich' },
      { label: 'Durchlaufzeit', value: '3,8 Tage', note: 'letzte 30 Tage' },
    ],
    workflow: ['Art wählen', 'Kunde', 'Maschine optional', 'Positionen', 'Übersicht & Freigabe'],
    queue: [
      { title: '202600132 · Toyota 8FBE16T', meta: 'Service · Werl Industriebedarf GmbH', status: 'Monteur zuweisen', priority: 'attention' },
      { title: '202600131 · Gabelzinken prüfen', meta: 'Interner Auftrag · ohne Maschine', status: 'Werkstatt', priority: 'normal' },
      { title: '202600128 · Verkaufsübergabe', meta: 'Sales → Orders', status: 'Bereit zur Abrechnung', priority: 'critical' },
    ],
    actions: ['Neuen Vorgang starten', 'Monteur zuweisen', 'Zur Abrechnung übergeben'],
  }),
  module({
    id: 'service',
    name: 'Service',
    route: '/service',
    permission: 'service.view',
    group: 'operations',
    readiness: 'interactive-preview',
    eyebrow: 'Service- und Werkstattsteuerung',
    description: 'Von Ankunft und Diagnose über Ersatzteile, UVV und Lackierung bis zur vollständigen Rückmeldung.',
    metrics: [
      { label: 'In Arbeit', value: '14', note: 'Werkstatt und Außendienst' },
      { label: 'Warten auf Teile', value: '5', note: '2 Beschaffungen kritisch' },
      { label: 'UVV fällig', value: '9', note: 'innerhalb 14 Tagen' },
      { label: 'Heute fertig', value: '4', note: 'Abnahme ausstehend' },
    ],
    workflow: ['Ankunft', 'Zuteilung', 'Diagnose & Teile', 'Reparatur / UVV', 'Abschluss & Rückmeldung'],
    queue: [
      { title: 'Toyota 8FBE16T · EPS prüfen', meta: 'Werkstattplatz 2 · Markus', status: 'Diagnose', priority: 'critical' },
      { title: 'Linde E35 · Hydraulik', meta: 'Außendienst · Frederick', status: 'Warten auf Teile', priority: 'attention' },
      { title: 'Jungheinrich EFG216 · UVV', meta: 'Werkstattplatz 4', status: 'Abnahme', priority: 'normal' },
    ],
    actions: ['Werkstattauftrag öffnen', 'Ersatzteilbedarf melden', 'Abschluss erfassen'],
    aliases: ['/workshop'],
  }),
  module({
    id: 'technician',
    name: 'Monteur',
    route: '/monteur',
    permission: 'technician.view',
    group: 'operations',
    readiness: 'interactive-preview',
    eyebrow: 'Mobiler Arbeitstag',
    description: 'Eigene Einsätze, Navigation, Zeiten, Material, Fotos, Unterschriften und Rückmeldungen auf einer mobilen Oberfläche.',
    metrics: [
      { label: 'Heute', value: '5 Einsätze', note: '2 bereits erledigt' },
      { label: 'Fahrzeit', value: '1:42 h', note: 'heutige Route' },
      { label: 'Arbeitszeit', value: '4:18 h', note: 'laufender Tag' },
      { label: 'Offene Rückmeldungen', value: '2', note: 'vor Feierabend' },
    ],
    workflow: ['Auftrag annehmen', 'Anfahrt', 'Arbeit starten', 'Material & Bericht', 'Unterschrift & Abschluss'],
    queue: [
      { title: '08:00 · UVV Toyota 8FBE16T', meta: 'Werl · 12 km', status: 'Erledigt', priority: 'normal' },
      { title: '11:30 · Störung Linde R16S', meta: 'Soest · 24 km', status: 'In Arbeit', priority: 'attention' },
      { title: '15:00 · Wartung BT RRE160', meta: 'Hamm · 31 km', status: 'Geplant', priority: 'normal' },
    ],
    actions: ['Zeit starten', 'Material hinzufügen', 'Kundenunterschrift erfassen'],
    aliases: ['/time-tracking'],
  }),
  module({
    id: 'rental',
    name: 'Mietpark',
    route: '/mietpark',
    permission: 'rental.view',
    group: 'operations',
    readiness: 'interactive-preview',
    eyebrow: 'Vermietung und Verfügbarkeit',
    description: 'Anfragen, Reservierungen, Mietaufträge, Auslieferung, Rücknahme, Schäden und Abrechnung.',
    metrics: [
      { label: 'Ausgelastet', value: '78 %', note: 'aktuelle Mietflotte' },
      { label: 'Heute raus', value: '3', note: 'Dokumente vollständig' },
      { label: 'Heute zurück', value: '2', note: 'Rücknahmeprüfung offen' },
      { label: 'Anfragen', value: '6', note: 'davon 2 dringend' },
    ],
    workflow: ['Anfrage', 'Verfügbarkeit', 'Bestätigung', 'Auslieferung', 'Rücknahme & Abrechnung'],
    queue: [
      { title: 'M-2026-00128 · Elektro 1,6 t', meta: 'Müller Logistik · 14 Tage', status: 'Auslieferung morgen', priority: 'attention' },
      { title: 'M-2026-00124 · Schubmast 2,0 t', meta: 'Hamm Lagertechnik', status: 'Laufende Miete', priority: 'normal' },
      { title: 'M-2026-00119 · Diesel 3,5 t', meta: 'Rücknahme heute', status: 'Schadenprüfung', priority: 'critical' },
    ],
    actions: ['Mietanfrage erfassen', 'Maschine reservieren', 'Rücknahme starten'],
  }),
  module({
    id: 'purchasing',
    name: 'Einkauf',
    route: '/einkauf',
    permission: 'purchasing.view',
    group: 'operations',
    readiness: 'interactive-preview',
    eyebrow: 'Beschaffung und Freigabe',
    description: 'Bedarfe bündeln, Angebote vergleichen, Freigaben einholen, bestellen und Wareneingänge abgleichen.',
    metrics: [
      { label: 'Offene Bedarfe', value: '18', note: '7 aus Mindestbestand' },
      { label: 'Zur Freigabe', value: '4', note: 'Gesamt 8.420 €' },
      { label: 'Bestellt', value: '11', note: '3 Liefertermine kritisch' },
      { label: 'Wareneingang offen', value: '6', note: 'heute erwartet' },
    ],
    workflow: ['Bedarf', 'Lieferant & Preis', 'Freigabe', 'Bestellung', 'Wareneingang'],
    queue: [
      { title: 'BED-2026-00042 · Hydraulikfilter', meta: 'TVH · 4 Stück', status: 'Freigabe', priority: 'attention' },
      { title: 'BEST-2026-00031 · Reifen', meta: 'Reifen Müller · 8 Stück', status: 'Bestellt', priority: 'normal' },
      { title: 'BEST-2026-00029 · Steuergerät', meta: 'Liefertermin überschritten', status: 'Nachfassen', priority: 'critical' },
    ],
    actions: ['Bedarf anlegen', 'Bestellung vorbereiten', 'Wareneingang buchen'],
  }),
  module({
    id: 'logistics',
    name: 'Spedition',
    route: '/spedition',
    permission: 'logistics.view',
    group: 'operations',
    readiness: 'interactive-preview',
    eyebrow: 'Transport- und Tourenplanung',
    description: 'Transportaufträge, Rollkarten, Fahrer, Fahrzeuge, Abholung, Auslieferung und Abliefernachweise.',
    metrics: [
      { label: 'Heute geplant', value: '12', note: '4 eigene, 8 Kundenaufträge' },
      { label: 'Unterwegs', value: '5', note: 'alle im Zeitfenster' },
      { label: 'Ohne Fahrer', value: '2', note: 'Zuweisung erforderlich' },
      { label: 'Nachweise offen', value: '3', note: 'Unterschrift oder Foto' },
    ],
    workflow: ['Transportbedarf', 'Rollkarte', 'Fahrer & Fahrzeug', 'Durchführung', 'Nachweis & Abrechnung'],
    queue: [
      { title: 'TR-2026-00218 · Toyota nach Dortmund', meta: '7,5 t · Fahrer: Sven', status: 'Unterwegs', priority: 'normal' },
      { title: 'TR-2026-00221 · Mietgerät abholen', meta: 'Soest → Werl', status: 'Fahrer fehlt', priority: 'critical' },
      { title: 'TR-2026-00217 · Gabelstapler ausliefern', meta: 'Sales-Auftrag AUF-2026-00001', status: 'Morgen 08:00', priority: 'attention' },
    ],
    actions: ['Transport anlegen', 'Rollkarte erzeugen', 'Fahrer zuweisen'],
  }),
  module({
    id: 'documents',
    name: 'Dokumente',
    route: '/documents',
    permission: 'documents.view',
    group: 'commercial',
    readiness: 'interactive-preview',
    eyebrow: 'FORQ Documents & Print Center',
    description: 'Vorlagen, Dokumenterzeugung, Versionen, Freigaben, E-Signatur, Druck und revisionssichere Ablage.',
    metrics: [
      { label: 'Heute erzeugt', value: '28', note: 'Angebote, Aufträge, Lieferscheine' },
      { label: 'Entwürfe', value: '7', note: '3 benötigen Freigabe' },
      { label: 'Vorlagen', value: '16', note: '3 Mandantenvarianten' },
      { label: 'Druckwarteschlange', value: '4', note: 'FORQ Print Center' },
    ],
    workflow: ['Vorlage wählen', 'Daten prüfen', 'Vorschau', 'Freigabe / Signatur', 'Versand & Archiv'],
    queue: [
      { title: 'ANG-2026-00001 · Angebot', meta: 'Werl Industriebedarf GmbH', status: 'Vorschau', priority: 'normal' },
      { title: 'LS-2026-00044 · Lieferschein', meta: 'Auslieferung morgen', status: 'Unterschrift offen', priority: 'attention' },
      { title: 'RE-2026-00128 · Rechnung', meta: 'E-Rechnung', status: 'Validierung', priority: 'critical' },
    ],
    actions: ['Dokument erzeugen', 'Vorlage bearbeiten', 'Druckauftrag starten'],
  }),
  module({
    id: 'finance',
    name: 'Finanzen',
    route: '/finance',
    permission: 'finance.view',
    group: 'commercial',
    readiness: 'interactive-preview',
    eyebrow: 'FORQ Finance',
    description: 'Ausgangsrechnungen, Eingangsrechnungen, Freigaben, Bank, DATEV, E-Rechnung und Mahnwesen.',
    metrics: [
      { label: 'Rechnungsentwürfe', value: '9', note: '5 aus Vorgängen' },
      { label: 'Offene Posten', value: '64.280 €', note: 'davon 8.900 € überfällig' },
      { label: 'Eingangsrechnungen', value: '12', note: '4 zur Zahlung freigegeben' },
      { label: 'Bank ungeklärt', value: '6', note: 'automatische Zuordnung offen' },
    ],
    workflow: ['Vorschlag übernehmen', 'Prüfen & freigeben', 'E-Rechnung / Versand', 'Zahlung zuordnen', 'DATEV & Mahnwesen'],
    queue: [
      { title: 'Verkauf AUF-2026-00001', meta: 'Sales-Übergabe', status: 'Rechnung erstellen', priority: 'critical' },
      { title: 'ER-2026-00411 · TVH', meta: '1.248,67 € · OCR geprüft', status: 'Zahlfreigabe', priority: 'attention' },
      { title: 'RE-2026-00119 · Mahnstufe 3', meta: 'Fällig seit 24 Tagen', status: 'Creditreform prüfen', priority: 'critical' },
    ],
    actions: ['Rechnung erstellen', 'Bankbewegungen prüfen', 'DATEV-Export vorbereiten'],
  }),
  module({
    id: 'messenger',
    name: 'Messenger',
    route: '/messenger',
    permission: 'messenger.view',
    group: 'collaboration',
    readiness: 'interactive-preview',
    eyebrow: 'Interne Zusammenarbeit',
    description: 'Einzel- und Gruppenchats, Antworten, Dateien, Erwähnungen, Lesestatus, Suche und direkter Vorgangsbezug.',
    metrics: [
      { label: 'Ungelesen', value: '7', note: '3 Erwähnungen' },
      { label: 'Aktive Gruppen', value: '11', note: 'Service, Sales, Spedition …' },
      { label: 'Vorgangschats', value: '6', note: 'automatisch verknüpft' },
      { label: 'Dateien heute', value: '14', note: 'Fotos und PDFs' },
    ],
    workflow: ['Chat wählen', 'Nachricht / Datei', 'Antwort & Erwähnung', 'Vorgang verknüpfen', 'Gelesen / erledigt'],
    queue: [
      { title: '#serviceleitung', meta: 'Markus: Ersatzteil ist angekommen', status: '2 ungelesen', priority: 'attention' },
      { title: 'Vorgang 202600132', meta: 'Werkstatt + Lager + Verwaltung', status: 'Foto hinzugefügt', priority: 'normal' },
      { title: '@Frederick', meta: 'Bitte Rechnungsvorschlag prüfen', status: 'Erwähnung', priority: 'critical' },
    ],
    actions: ['Neue Nachricht', 'Gruppe anlegen', 'Vorgangschat öffnen'],
  }),
  module({
    id: 'feedback',
    name: 'Feedback',
    route: '/feedback',
    permission: 'feedback.view',
    group: 'collaboration',
    readiness: 'interactive-preview',
    eyebrow: 'Bugs, Wünsche und Verbesserungen',
    description: 'Beobachtungen direkt aus FORQ erfassen, priorisieren, zuordnen und bis zur Lösung nachvollziehen.',
    metrics: [
      { label: 'Neue Meldungen', value: '8', note: 'seit Montag' },
      { label: 'Kritische Bugs', value: '2', note: 'Deployment und Rechnung' },
      { label: 'In Umsetzung', value: '11', note: 'aktuelle Iteration' },
      { label: 'Gelöst', value: '34', note: 'letzte 30 Tage' },
    ],
    workflow: ['Beobachtung erfassen', 'Bug / Wunsch', 'Priorisieren', 'Umsetzen & testen', 'Gelöst / Patchnote'],
    queue: [
      { title: 'Rechnungsvorschlag verschwindet', meta: 'Finance · Sales-Übergabe', status: 'In Umsetzung', priority: 'critical' },
      { title: 'Kundensuche mobil zu klein', meta: 'Customers · Tablet', status: 'Geplant', priority: 'attention' },
      { title: 'Schnellaktion UVV', meta: 'Service', status: 'Idee', priority: 'normal' },
    ],
    actions: ['Bug melden', 'Feature wünschen', 'Testfeedback erfassen'],
  }),
  module({
    id: 'forqy',
    name: 'FORQY',
    route: '/forqy',
    permission: 'forqy.view',
    group: 'collaboration',
    readiness: 'platform-preview',
    eyebrow: 'Hilfe und Wissensassistent',
    description: 'Kontextbezogene Hilfe, Anleitungen, Patch Notes, Playbooks und später sichere Antworten aus der FORQ-Wissensbasis.',
    metrics: [
      { label: 'Hilfeartikel', value: '86', note: 'nach Modul und Rolle' },
      { label: 'Playbooks', value: '14', note: 'Schritt-für-Schritt' },
      { label: 'Neue Patch Notes', value: '5', note: 'noch nicht gelesen' },
      { label: 'Gelöste Fragen', value: '92 %', note: 'Demo-Auswertung' },
    ],
    workflow: ['Frage stellen', 'Kontext erkennen', 'Artikel / Playbook', 'Schritt ausführen', 'Feedback geben'],
    queue: [
      { title: 'Wie übergebe ich einen Verkauf?', meta: 'Sales → Finance & Logistics', status: 'Playbook verfügbar', priority: 'normal' },
      { title: 'Kunde ohne Maschine anlegen', meta: 'Orders', status: 'Kurzanleitung', priority: 'normal' },
      { title: 'Was ist neu in FORQ?', meta: 'Patch Notes', status: '5 Neuigkeiten', priority: 'attention' },
    ],
    actions: ['FORQY fragen', 'Hilfe zu dieser Seite', 'Patch Notes öffnen'],
  }),
  module({
    id: 'studio',
    name: 'FORQ Studio',
    route: '/studio',
    permission: 'studio.runtime.configure',
    group: 'system',
    readiness: 'platform-preview',
    eyebrow: 'No-Code UI- und Modulkonfiguration',
    description: 'Layouts, Frames, Seiten, Widgets, Felder, Rollenansichten und Themes konfigurieren, ohne Fachlogik zu verändern.',
    metrics: [
      { label: 'Zielseiten', value: '18', note: 'zentral steuerbar' },
      { label: 'App Shells', value: '2', note: 'Standard und Kompakt' },
      { label: 'Themes', value: '2', note: 'Dark Red und Kontrast' },
      { label: 'Änderungen offen', value: '3', note: 'noch nicht veröffentlicht' },
    ],
    workflow: ['Zielseite wählen', 'Layout / Frame', 'Bereiche & Widgets', 'Rollen-Vorschau', 'Prüfen & veröffentlichen'],
    queue: [
      { title: 'Sales · Kompaktansicht', meta: 'Vertrieb', status: 'Entwurf', priority: 'normal' },
      { title: 'Monteur · Mobile Tagesansicht', meta: 'Rolle Monteur', status: 'Vorschau', priority: 'attention' },
      { title: 'Management Dashboard', meta: 'Geschäftsführung', status: 'Freigabe offen', priority: 'normal' },
    ],
    actions: ['Seite konfigurieren', 'Rollen-Vorschau', 'Änderungen veröffentlichen'],
  }),
];

export const completeCatalog: readonly CompleteCatalogEntry[] = [
  { id: 'dashboard', name: 'Dashboard', route: '/dashboard', group: 'start', readiness: 'foundation', description: 'Persönlicher Einstieg, Kennzahlen, Aufgaben und Schnellaktionen.' },
  { id: 'orders', name: 'Vorgänge', route: '/vorgaenge', group: 'operations', readiness: 'interactive-preview', description: 'Gemeinsame Vorgangszentrale für Service, intern, Verkauf und Miete.' },
  { id: 'customers', name: 'Kunden', route: '/kunden', group: 'masterdata', readiness: 'foundation', description: 'Kunden, Anschriften, Ansprechpartner und Dublettenprüfung.' },
  { id: 'machines', name: 'Maschinen', route: '/maschinen', group: 'masterdata', readiness: 'foundation', description: 'Maschinenstamm, Kundenbezug und dynamische technische Daten.' },
  { id: 'service', name: 'Service', route: '/service', group: 'operations', readiness: 'interactive-preview', description: 'Service- und Werkstattsteuerung.', aliases: ['/workshop'] },
  { id: 'services', name: 'Dienstleistungen', route: '/dienstleistungen', group: 'masterdata', readiness: 'foundation', description: 'Wiederverwendbare Leistungen, Einheiten und Verkaufspreise.' },
  { id: 'technician', name: 'Monteur', route: '/monteur', group: 'operations', readiness: 'interactive-preview', description: 'Mobile Einsätze, Zeit, Material und Rückmeldung.', aliases: ['/time-tracking'] },
  { id: 'sales', name: 'Sales', route: '/verkauf', group: 'commercial', readiness: 'foundation', description: 'Pipeline, Angebote, Verkaufsaufträge und Übergaben.' },
  { id: 'rental', name: 'Mietpark', route: '/mietpark', group: 'operations', readiness: 'interactive-preview', description: 'Mietanfragen, Reservierung, Auslieferung und Rücknahme.' },
  { id: 'warehouse', name: 'Lager', route: '/lager', group: 'operations', readiness: 'foundation', description: 'Lagerplätze, Bestände, Bewegungen und Beschaffungsvorschläge.' },
  { id: 'purchasing', name: 'Einkauf', route: '/einkauf', group: 'operations', readiness: 'interactive-preview', description: 'Bedarfe, Freigaben, Bestellungen und Wareneingang.' },
  { id: 'suppliers', name: 'Lieferanten', route: '/lieferanten', group: 'masterdata', readiness: 'foundation', description: 'Lieferantenstamm und lieferantenspezifische Artikel.' },
  { id: 'logistics', name: 'Spedition', route: '/spedition', group: 'operations', readiness: 'interactive-preview', description: 'Transportaufträge, Rollkarten und Abliefernachweise.' },
  { id: 'documents', name: 'Dokumente', route: '/documents', group: 'commercial', readiness: 'interactive-preview', description: 'Vorlagen, Dokumente, Signatur, Druck und Archiv.' },
  { id: 'finance', name: 'Finanzen', route: '/finance', group: 'commercial', readiness: 'interactive-preview', description: 'Rechnungen, Bank, DATEV, Freigaben und Mahnwesen.' },
  { id: 'messenger', name: 'Messenger', route: '/messenger', group: 'collaboration', readiness: 'interactive-preview', description: 'Chats, Gruppen, Dateien, Erwähnungen und Vorgangsbezug.' },
  { id: 'feedback', name: 'Feedback', route: '/feedback', group: 'collaboration', readiness: 'interactive-preview', description: 'Bugs, Wünsche, Backlog und Testfeedback.' },
  { id: 'admin', name: 'Administration', route: '/admin', group: 'system', readiness: 'foundation', description: 'Benutzer, Rollen, Rechte, Nummernkreise und Audit.' },
  { id: 'forqy', name: 'FORQY', route: '/forqy', group: 'collaboration', readiness: 'platform-preview', description: 'Kontextbezogene Hilfe, Wissensbasis und Patch Notes.' },
  { id: 'studio', name: 'FORQ Studio', route: '/studio', group: 'system', readiness: 'platform-preview', description: 'No-Code UI-, Layout- und Rollenansichten.' },
];

export function previewDefinition(id: string): PreviewModuleDefinition | undefined {
  return previewModuleDefinitions.find((definition) => definition.id === id);
}
